// Play time per tune (Oct 2026): the half of `tuneHistory` that comes from the
// transport. While the band plays a tune, the seconds accrue to that tune's
// history, keyed by its identity as the band started, so a tune you load next
// (which stops the band) is never charged for the one before. The standalone
// metronome plays no tune and charges nothing. Time is banked when the band
// pauses or stops, when the page hides (background playback keeps counting),
// and every half minute while it plays, so a closed tab loses little.

import { tuneIdentityOf, useStore } from '@/state/store';
import type { PlaylistTuneRef } from '@/storage/playlists';

export interface PlayHistoryPlayer {
  onTransportChange(listener: (state: 'idle' | 'playing' | 'paused') => void): () => void;
  playingTune(): boolean;
}

/** How often play time is banked while the band plays. */
export const BANK_EVERY_MS = 30_000;

interface Playing {
  key: string;
  tune: { title: string; composer?: string; ref?: PlaylistTuneRef | null };
  since: number;
}

/**
 * Count the band's play time against the tune it plays. Returns a disposer;
 * App calls it once.
 */
export function initPlayHistory(player: PlayHistoryPlayer): () => void {
  let playing: Playing | null = null;
  let timer: ReturnType<typeof setInterval> | null = null;

  const bank = (keepGoing: boolean) => {
    if (!playing) return;
    const now = Date.now();
    const seconds = (now - playing.since) / 1000;
    const { key, tune } = playing;
    playing = keepGoing ? { ...playing, since: now } : null;
    if (!keepGoing && timer !== null) {
      clearInterval(timer);
      timer = null;
    }
    if (seconds >= 1) useStore.getState().recordTunePlay(key, tune, seconds);
  };

  const unsubscribe = player.onTransportChange((state) => {
    if (state !== 'playing') {
      bank(false);
      return;
    }
    if (playing || !player.playingTune()) return;
    const s = useStore.getState();
    playing = {
      key: s.currentTuneKey ?? tuneIdentityOf(s.score, s.chart),
      tune: {
        title: s.chart.title || s.score.title,
        ...(s.chart.composer ? { composer: s.chart.composer } : {}),
        ref: s.currentTuneRef,
      },
      since: Date.now(),
    };
    timer = setInterval(() => bank(true), BANK_EVERY_MS);
  });

  const onHide = () => {
    if (document.visibilityState === 'hidden') bank(true);
  };
  const onPageHide = () => bank(true);
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', onPageHide);
  }

  return () => {
    bank(false);
    unsubscribe();
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', onPageHide);
    }
  };
}
